import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import CaseContextHeader from "../components/CaseContextHeader";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { downloadAuthed } from "../lib/download";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Save, Send, MessageSquarePlus, History, ShieldAlert, Send as SendIcon, Stamp,
  Download, RefreshCw,
} from "lucide-react";
import { ENGINE_LABEL, DISPLAY_MODEL } from "../lib/brand";

export default function ReviewWorkspace() {
  const params = useParams();
  const navigate = useNavigate();
  const [draftId, setDraftId] = useState(params.draftId || "");
  const [caseId, setCaseId] = useState(params.caseId || "");
  const [drafts, setDrafts] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [caseDoc, setCaseDoc] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [edits, setEdits] = useState({});
  const [comment, setComment] = useState({ text: "", type: "Comment", priority: "Normal" });

  const reload = async () => {
    if (!draftId) return;
    const { data } = await api.get(`/drafts/${draftId}`);
    setBundle(data);
    setCaseId(data.draft.case_id);
    const c = await api.get(`/cases/${data.draft.case_id}`);
    setCaseDoc(c.data.case);
    setEdits(Object.fromEntries(data.sections.map((s) => [s.draft_section_id, s.section_text])));
  };

  useEffect(() => {
    if (!draftId && caseId) {
      api.get(`/cases/${caseId}/drafts`).then((r) => setDrafts(r.data));
    } else if (!draftId) {
      api.get("/cases").then(async (r) => {
        const all = [];
        for (const c of r.data.slice(0, 20)) {
          const d = await api.get(`/cases/${c.case_id}/drafts`);
          d.data.forEach((dr) => all.push({ ...dr, case_number: c.case_number, case_year: c.case_year }));
        }
        setDrafts(all);
      });
    } else {
      reload();
    }
  }, [draftId]); // eslint-disable-line

  const saveSection = async (s) => {
    try {
      await api.patch(`/drafts/sections/${s.draft_section_id}`, {
        section_text: edits[s.draft_section_id],
        edit_reason: "Reviewer edit",
      });
      toast.success("Section saved");
      reload();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const addComment = async () => {
    if (!comment.text.trim()) return;
    await api.post(`/drafts/${draftId}/comments`, {
      draft_section_id: activeSection?.draft_section_id || null,
      comment_text: comment.text,
      comment_type: comment.type,
      priority: comment.priority,
    });
    setComment({ ...comment, text: "" });
    reload();
  };

  const submitForApproval = async () => {
    await api.post(`/drafts/${draftId}/submit-for-approval`);
    toast.success("Sent to officer for approval");
    reload();
  };

  const exportDraft = async (fmt) => {
    const { data } = await api.post(`/drafts/${draftId}/export`, null, { params: { fmt } });
    await downloadAuthed(`/exports/${data.export_id}/download`, data.file_name);
    toast.success(`${fmt.toUpperCase()} exported`);
  };

  const goToSection = (s) => {
    setActiveSection(s);
    document.getElementById(s.draft_section_id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!draftId) {
    return (
      <Layout breadcrumbs={["SSRD", "Review Workspace"]}>
        <h1 className="text-2xl font-bold mb-4">Pick a draft to review</h1>
        <Card><CardContent className="p-0"><Table>
          <TableHeader><TableRow><TableHead>Case</TableHead><TableHead>Version</TableHead><TableHead>Engine</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {drafts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No drafts yet — generate one first.</TableCell></TableRow>}
            {drafts.map((d) => (
              <TableRow key={d.draft_version_id}>
                <TableCell className="font-mono text-xs">{d.case_number ? `${d.case_number}/${d.case_year}` : d.case_id.slice(0, 8)}</TableCell>
                <TableCell>v{d.version_no}</TableCell>
                <TableCell className="text-xs font-mono">{DISPLAY_MODEL}</TableCell>
                <TableCell><StatusBadge status={d.draft_status} /></TableCell>
                <TableCell><Button size="sm" onClick={() => setDraftId(d.draft_version_id)}>Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></CardContent></Card>
      </Layout>
    );
  }

  if (!bundle) return <Layout breadcrumbs={["SSRD", "Review"]}><div>Loading…</div></Layout>;
  const { sections, traceability, quality_checks, comments, edits: editHistory, draft, approval } = bundle;

  return (
    <Layout breadcrumbs={[
      { label: "SSRD", to: "/dashboard" },
      { label: "Cases", to: "/cases" },
      caseDoc ? { label: `${caseDoc.case_number}/${caseDoc.case_year}`, to: `/cases/${caseDoc.case_id}` } : "…",
      `Draft v${draft.version_no}`,
    ]}>
      <CaseContextHeader caseDoc={caseDoc} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="kbd">{ENGINE_LABEL} · {DISPLAY_MODEL}</span>
          <StatusBadge status={draft.draft_status} />
          <span className="text-xs text-muted-foreground">{(draft.generated_at || "").replace("T", " ").slice(0, 16)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={reload} className="gap-1"><RefreshCw className="size-3.5" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => exportDraft("docx")} className="gap-1" data-testid="export-docx-btn"><Download className="size-3.5" /> DOCX</Button>
          <Button variant="outline" size="sm" onClick={() => exportDraft("pdf")} className="gap-1" data-testid="export-pdf-btn"><Download className="size-3.5" /> PDF</Button>
          <Button size="sm" onClick={submitForApproval} className="gap-1" data-testid="submit-officer-btn"><SendIcon className="size-3.5" /> Submit to Officer</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Editor (left) */}
        <Card className="overflow-hidden" data-testid="draft-editor-card">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2 overflow-x-auto">
              {sections.map((s) => (
                <button
                  key={s.draft_section_id}
                  onClick={() => goToSection(s)}
                  className={`text-xs whitespace-nowrap rounded-md px-2.5 py-1.5 border transition-colors ${activeSection?.draft_section_id === s.draft_section_id ? "bg-primary text-white border-primary" : "bg-card border-border hover:bg-secondary"}`}
                  data-testid={`section-tab-${s.section_key}`}
                >
                  {s.section_name}
                </button>
              ))}
            </div>
            <div className="p-5 space-y-6">
              {sections.map((s) => (
                <div key={s.draft_section_id} id={s.draft_section_id} className="scroll-mt-24">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-widest text-muted-foreground uppercase">{s.section_key}</div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={s.review_status} />
                      <Button size="sm" variant="outline" onClick={() => saveSection(s)} className="gap-1" data-testid={`save-section-${s.section_key}`}><Save className="size-3.5" /> Save</Button>
                    </div>
                  </div>
                  <h3 className="text-base font-semibold mb-2">{s.section_name}</h3>
                  <Textarea
                    rows={Math.max(6, Math.min(20, (edits[s.draft_section_id] || "").split("\n").length + 1))}
                    className="gujarati"
                    value={edits[s.draft_section_id] || ""}
                    onChange={(e) => setEdits({ ...edits, [s.draft_section_id]: e.target.value })}
                    data-testid={`section-textarea-${s.section_key}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right: tabs */}
        <div className="space-y-4">
          <Tabs defaultValue="quality">
            <TabsList className="grid grid-cols-4">
              <TabsTrigger value="quality" data-testid="rw-tab-quality">QC</TabsTrigger>
              <TabsTrigger value="trace" data-testid="rw-tab-trace">Trace</TabsTrigger>
              <TabsTrigger value="comments" data-testid="rw-tab-comments">Notes</TabsTrigger>
              <TabsTrigger value="history" data-testid="rw-tab-history">Edits</TabsTrigger>
            </TabsList>

            <TabsContent value="quality">
              <Card><CardContent className="p-4 space-y-2">
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Quality Checks</div>
                {quality_checks.map((q) => (
                  <div key={q.quality_check_id} className="flex items-start justify-between gap-2 text-xs border border-border rounded-md p-2">
                    <div>
                      <div className="font-mono text-[11px]">{q.check_type}</div>
                      {q.issue_description && <div className="text-muted-foreground">{q.issue_description}</div>}
                    </div>
                    <StatusBadge status={q.check_result === "Passed" ? "Completed" : "Failed"} />
                  </div>
                ))}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="trace">
              <Card><CardContent className="p-4 space-y-2">
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Traceability Map</div>
                {traceability.map((t) => (
                  <div key={t.traceability_id} className="text-xs border border-border rounded-md p-2">
                    <div className="font-mono text-[11px] text-primary">{t.source_field_name}</div>
                    <div className="text-muted-foreground">Source: {t.source_type} · Page {t.source_page_no} · conf {t.confidence_score}</div>
                  </div>
                ))}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="comments">
              <Card><CardContent className="p-4 space-y-3">
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Reviewer Notes</div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {comments.length === 0 && <div className="text-xs text-muted-foreground">No comments.</div>}
                  {comments.map((c) => (
                    <div key={c.comment_id} className="text-xs border border-border rounded-md p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium">{c.author_name} <span className="text-muted-foreground font-normal">· {c.author_role}</span></div>
                        <span className="text-[10px] text-muted-foreground">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div>{c.comment_text}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 pt-2 border-t border-border">
                  <Textarea rows={2} placeholder={activeSection ? `Comment on ${activeSection.section_name}…` : "Comment on draft…"}
                    value={comment.text} onChange={(e) => setComment({ ...comment, text: e.target.value })}
                    data-testid="comment-textarea" />
                  <Button size="sm" onClick={addComment} className="w-full gap-1" data-testid="add-comment-btn"><MessageSquarePlus className="size-3.5" /> Add comment</Button>
                </div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="history">
              <Card><CardContent className="p-4 space-y-2">
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Edit History</div>
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {editHistory.length === 0 && <div className="text-xs text-muted-foreground">No edits yet.</div>}
                  {editHistory.map((e) => (
                    <div key={e.edit_id} className="text-xs border border-border rounded-md p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium">{e.edited_by_user_name} edited {e.section_name}</div>
                        <span className="text-[10px] text-muted-foreground">{(e.edited_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div className="text-muted-foreground">Reason: {e.edit_reason}</div>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            </TabsContent>
          </Tabs>

          {approval && (
            <Card><CardContent className="p-4 text-xs space-y-1">
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Officer Approval</div>
              <div><strong>Status:</strong> <StatusBadge status={approval.approval_status} /></div>
              <div><strong>By:</strong> {approval.officer_name} ({approval.officer_designation})</div>
              {approval.officer_remarks && <div><strong>Remarks:</strong> {approval.officer_remarks}</div>}
            </CardContent></Card>
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3 text-[11px] flex gap-2">
            <ShieldAlert className="size-4 shrink-0 mt-0.5" />
            Remi8-generated draft is not final until reviewed and approved by the authorized officer.
          </div>
        </div>
      </div>
    </Layout>
  );
}
